import { INDUSTRIES } from '@cyberscore/contracts';
import { GitBranch, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useTryFlow, type TryFlowResult } from './api';

/**
 * Try a journey with made-up answers (task 85): pick answers and watch the routes take the flow forward,
 * skip questions and stages, or count questions as not in place. Uses the unsaved edits too; nothing is
 * stored.
 */
const STATUS: Record<TryFlowResult['steps'][number]['status'], string> = {
  answered: 'Answered',
  to_answer: 'To answer',
  waiting: 'Waiting',
  skipped: 'Skipped',
  implied: 'Not in place',
  not_applicable: 'Doesn’t apply',
  not_reached: 'Not reached',
};

export function TryFlow({
  journeyKey,
  edits,
}: {
  journeyKey: string;
  edits: { journey?: unknown; stages: Record<string, unknown> };
}) {
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [industry, setIndustry] = useState('manufacturing');
  const flow = useTryFlow({ journey: journeyKey, answers, facts: { industry, country: 'MY' }, edits });
  const answer = (q: string, v: unknown) =>
    setAnswers(({ [q]: _, ...rest }) => (v === undefined ? rest : { ...rest, [q]: v }));
  const data = flow.data;

  return (
    <section className="card pad try-flow" aria-labelledby="try-h">
      <div className="row-between">
        <h2 id="try-h" className="section-title row gap">
          <GitBranch size={16} /> Try the flow
        </h2>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAnswers({})} disabled={!Object.keys(answers).length}>
          <RotateCcw size={14} /> Start again
        </button>
      </div>
      <p className="muted small">Answer as a business would and see where the routes take it. Unsaved changes are included; nothing is saved.</p>
      <div className="field">
        <label htmlFor="try-industry">Industry</label>
        <select id="try-industry" className="input" value={industry} onChange={(e) => setIndustry(e.target.value)}>
          {Object.entries(INDUSTRIES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>
      {flow.isError && <div className="notice warn">{flow.error instanceof ApiError ? flow.error.message : 'Could not work out the flow'}</div>}
      {data && (
        <>
          <p className="small" aria-live="polite">
            <b>{data.state === 'completed' ? 'Complete.' : `${data.counts.to_answer + data.counts.waiting} to answer.`}</b>{' '}
            {data.counts.answered} answered · {data.counts.skipped} skipped · {data.counts.implied} not in place
          </p>
          {data.taken.length > 0 && (
            <ul className="path-list" aria-label="Routes taken">
              {data.taken.map((t) => (
                <li key={t.route}>
                  <b>{t.label}</b>
                  <span className="muted small">
                    after {t.from}: {t.skipped.length} skipped, {t.implied.length} not in place
                  </span>
                </li>
              ))}
            </ul>
          )}
          {data.stages.map((s) => {
            const steps = data.steps.filter((x) => x.stage === s.key && x.status !== 'not_applicable');
            const open = s.state === 'available' || s.state === 'active';
            return (
              <details key={s.key} className="try-stage" open={open}>
                <summary>
                  <b>{s.name}</b> <span className="muted small">· {s.state === 'skipped' && s.routed_by ? `skipped: ${s.routed_by.label}` : s.state}</span>
                </summary>
                <ol className="try-steps">
                  {steps.map((x) => (
                    <li key={x.question} className={`try-step ${x.status}`}>
                      <span className="mono small">{x.question}</span>
                      <span className="grow small">{x.text}</span>
                      {x.status === 'to_answer' || x.status === 'answered' ? (
                        <Answer step={x} value={answers[x.question]} onChange={(v) => answer(x.question, v)} />
                      ) : (
                        <span className="pill neutral" title={x.by ? `${x.by.label} (after ${x.by.from})` : undefined}>
                          {STATUS[x.status]}
                          {x.by ? `: ${x.by.label}` : ''}
                        </span>
                      )}
                    </li>
                  ))}
                  {!steps.length && <li className="muted small">Nothing asked here.</li>}
                </ol>
              </details>
            );
          })}
        </>
      )}
    </section>
  ); // prettier-ignore
}

function Answer({
  step,
  value,
  onChange,
}: {
  step: TryFlowResult['steps'][number];
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = `Answer ${step.question}`;
  if (step.options.length)
    return (
      <select className="input input-sm" aria-label={label} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || undefined)}>
        <option value="">–</option>
        {step.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    ); // prettier-ignore
  const numeric = ['numeric', 'employee_count', 'percentage'].includes(step.type);
  return (
    <input
      className="input input-sm try-input"
      aria-label={label}
      type={numeric ? 'number' : 'text'}
      value={
        value === undefined
          ? ''
          : String(typeof value === 'object' ? ((value as { amount?: number }).amount ?? '') : value)
      }
      onChange={(e) => {
        const v = e.target.value;
        if (!v) return onChange(undefined);
        onChange(step.type === 'currency' ? { amount: Number(v), currency: 'MYR' } : numeric ? Number(v) : v);
      }}
    />
  );
}
